import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Textarea } from '@mantine/core';
import { Button } from '../../../../../components/Button/Button';
import classes from './daily-brief-modal.module.scss';

export type DailyBriefModalProps = {
  opened: boolean;
  onClose: () => void;
  dailyBrief: string | null;
  isManager: boolean;
  onSave: (text: string) => void;
  isSaving: boolean;
};

// Summary: Daily-brief modal — manager edits (textarea + אישור/ביטול), others read-only.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-areas
const DailyBriefModalComponent: React.FC<DailyBriefModalProps> = ({
  opened,
  onClose,
  dailyBrief,
  isManager,
  onSave,
  isSaving,
}) => {
  const [draft, setDraft] = useState(dailyBrief ?? '');

  useEffect(() => {
    if (opened) {
      setDraft(dailyBrief ?? '');
    }
  }, [opened, dailyBrief]);

  const handleSave = useCallback(() => {
    if (isSaving) {
      return;
    }
    // No-op guard (Rule 24): unchanged brief closes without a PATCH/SSE/toast to every tab.
    if (draft === (dailyBrief ?? '')) {
      onClose();
      return;
    }
    onSave(draft);
  }, [draft, dailyBrief, isSaving, onClose, onSave]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="בריף יומי"
      size="md"
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: classes.modalRoot }}
      withCloseButton={isManager ? false : true}
      closeButtonProps={{ 'data-testid': 'daily-brief-close' } as Record<string, string>}
    >
      <div className={classes.body}>
        {isManager ? (
          <Textarea
            data-testid="daily-brief-textarea"
            placeholder="כתוב כאן את הבריף היומי לכל הצוות"
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onContextMenu={(event) => event.preventDefault()}
            onSelect={(event) => {
              const input = event.currentTarget;
              const end = input.value.length;
              if (input.selectionStart !== end || input.selectionEnd !== end) {
                input.setSelectionRange(end, end);
              }
            }}
            className={classes.textarea}
            aria-label="תוכן בריף יומי"
            autosize
            minRows={5}
            maxRows={12}
            maxLength={2000}
            data-autofocus
          />
        ) : dailyBrief && dailyBrief.trim() !== '' ? (
          <p className={classes.readonlyText} onContextMenu={(event) => event.preventDefault()}>{dailyBrief}</p>
        ) : (
          <p className={classes.emptyHint} onContextMenu={(event) => event.preventDefault()}>אין בריף יומי כרגע.</p>
        )}

        {isManager && (
          <div className={classes.buttonRow}>
            <Button variant="cancel" onClick={onClose} disabled={isSaving} fullWidth dataTestId="daily-brief-cancel">
              ביטול
            </Button>
            <Button
              variant="confirm"
              onClick={handleSave}
              loading={isSaving}
              loadingLabel="טוען..."
              fullWidth
              dataTestId="daily-brief-confirm"
            >
              אישור
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export const DailyBriefModal = React.memo(DailyBriefModalComponent);
